import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VehicleListComponent } from '../vehicle-list/vehicle-list.component';
import { VehicleService } from '../../services/vehicle.service';
import { PackageService } from '../../services/package.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { extractErrorMessage } from '../../shared/utils/error.util';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';

/**
 * CustomerInformationFormComponent manages customer details and vehicle entry workflow across a multi-step form, coordinating creation of customers and vehicles and emitting navigation events to parent components.
 * @component
 * @selector app-customer-information-form
 * @example
 *   <app-customer-information-form
 *     [titleHeading]="'New Customer'"
 *     [requiresValidation]="true"
 *     [shouldCreateCustomer]="false"
 *     [currentStep]="1"
 *     (onContinue)="handleContinue()"
 *     (saveNewVehicle)="handleVehicleSaved($event)">
 *   </app-customer-information-form>
 * @input {string} titleHeading - Title shown at the top of the form.
 * @input {boolean} requiresValidation - Whether the form requires validation before continuing.
 * @input {boolean} shouldCreateCustomer - When true, the component will emit createCustomer to trigger customer creation before continuing.
 * @input {boolean} hasExistingVehicles - True if the customer already has vehicles in the system.
 * @input {boolean} isAddingNewVehicle - True when the UI is in the state of adding a new vehicle.
 * @input {number} currentStep - Current step index in the parent multi-step workflow.
 * @input {boolean} isDisabled - Disables form actions when true.
 * @input {string} customerId - The created customer's ID (used when saving vehicles).
 * @input {number} vehicleCount - Number of vehicles currently associated with the customer.
 * @output {EventEmitter<void>} onContinue - Emitted to request the parent to advance to the next step.
 * @output {EventEmitter<void>} createCustomer - Emitted to request customer creation when required before continuing.
 * @output {EventEmitter<void>} addNewVehicle - Emitted when user requests to start adding a new vehicle.
 * @output {EventEmitter<any>} vehicleFormDataChange - Emits intermediate vehicle form data changes to the parent.
 * @output {EventEmitter<void>} cancelAddVehicle - Emitted when adding a vehicle is canceled.
 * @output {EventEmitter<any>} saveNewVehicle - Emitted after attempting to save a new vehicle; payload indicates success and includes vehicle data or error info.
 * @output {EventEmitter<void>} onBack - Emitted when the user navigates to the previous step.
 * @output {EventEmitter<void>} onCancel - Emitted when the entire flow is canceled.
 *
 * Key methods:
 * - Continue(): Handles validation and determines whether to emit onContinue or createCustomer; sets a processing state for step 4.
 * - onFormValidityChange(isValid): Updates local form validity flags used to gate continuation.
 * - onCustomerCreated(customerData): Called after customer creation to continue the flow.
 * - onProcessingStateChange(isProcessing): Updates local processing indicator (isProcessing).
 * - onAddNewVehicle(): Emits addNewVehicle to notify parent to show vehicle creation flow.
 * - onVehicleFormDataChange(data): Forwards vehicle form data changes to the parent.
 * - onVehicleCreatedManually(vehicleData): Emits saveNewVehicle with a success payload when a manual vehicle is created.
 * - onManualFieldsVisibilityChange(isVisible): Tracks visibility of manual vehicle entry fields.
 * - onCancelAddVehicle(): Emits cancelAddVehicle to notify parent to close vehicle add UI.
 * - onSaveNewVehicle(): Validates presence of customerId and a child vehicle form component, invokes creation via the child (addVehicleComponent.createVehicleForCustomer), shows success/error messages via NotificationService, and emits saveNewVehicle with detailed results.
 * - onBackClick() / onCancelClick(): Emit onBack and onCancel respectively.
 *
 * Important internals and lifecycle notes:
 * - Uses a @ViewChild reference (addVehicleComponent) to interact with the child VehicleListComponent for creating vehicles.
 * - Injects VehicleService, PackageService and NotificationService in the constructor; no explicit Angular lifecycle hooks are implemented in this component but constructor initializes hasPackages$ observable.
 * - hasPackages$ is an observable derived from PackageService to indicate whether any active packages exist.
 * - Active-package availability only gates Continue on the package-selection step (currentStep === 1); booking, customer, and vehicle steps use requiresValidation / isFormValid instead.
 * - The component maintains local UI state flags: isFormValid, isProcessing, step4FormValid and showManualVehicleFields.
 */
@Component({
  selector: 'app-customer-information-form',
  templateUrl: './customer-information-form.component.html',
  styleUrls: ['./customer-information-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, VehicleListComponent, ModalFooterActionsComponent],
})
export class CustomerInformationFormComponent {
  @ViewChild('addVehicleComponent', { static: false }) addVehicleComponent!: VehicleListComponent;

  @Output() continueStep = new EventEmitter<void>();
  @Output() createCustomer = new EventEmitter<void>();
  @Output() addNewVehicle = new EventEmitter<void>();
  @Output() vehicleFormDataChange = new EventEmitter<any>();
  @Output() cancelAddVehicle = new EventEmitter<void>();
  @Output() saveNewVehicle = new EventEmitter<any>();
  @Output() back = new EventEmitter<void>();
  @Output() cancelFlow = new EventEmitter<void>();

  @Input() titleHeading = '';
  @Input() requiresValidation = false;
  @Input() shouldCreateCustomer = false;
  @Input() hasExistingVehicles = false;
  @Input() isAddingNewVehicle = false;
  @Input() currentStep = 0;
  @Input() isDisabled = false;
  @Input() customerId = '';
  @Input() vehicleCount = 0;

  isFormValid = false;
  isProcessing = false;
  step4FormValid = false;
  hasPackages$: Observable<boolean>;
  showManualVehicleFields = false; // Track if manual vehicle fields are visible

  /** Select Detailing (step 1) is the only step that requires active packages to Continue. */
  get requiresActivePackage(): boolean {
    return this.currentStep === 1;
  }

  constructor(
    private vehicleService: VehicleService,
    private packageService: PackageService,
    private messageService: NotificationService
  ) {
    this.hasPackages$ = this.packageService.packages$.pipe(
      map(packages => packages.filter(p => p.package_status === true).length > 0)
    );
  }

  /**
   * Handle transitioning to the next step: if validation passes, emit continue or create-customer events and set processing when on the final step.
   * @example
   * ontinue()
   * undefined
   * @returns {void} Does not return a value; may set isProcessing and emit onContinue or createCustomer events based on state.
   **/
  Continue(): void {
    if (!this.requiresValidation || this.isFormValid) {
      if (this.currentStep === 4) {
        // For step 4, set processing state and emit to parent
        this.isProcessing = true;
        this.continueStep.emit();
      } else if (this.shouldCreateCustomer) {
        this.isProcessing = true;
        this.createCustomer.emit();
      } else {
        this.continueStep.emit();
      }
    }
  }

  onFormValidityChange(isValid: boolean): void {
    this.isFormValid = isValid;
    this.step4FormValid = isValid; // Also update step4FormValid for vehicle form
  }

  onCustomerCreated(customerData: any): void {
    // Customer was created successfully, now continue
    this.continueStep.emit();
  }

  onProcessingStateChange(isProcessing: boolean): void {
    this.isProcessing = isProcessing;
  }

  onAddNewVehicle(): void {
    this.addNewVehicle.emit();
  }

  onVehicleFormDataChange(data: any): void {
    this.vehicleFormDataChange.emit(data);
  }

  onVehicleCreatedManually(vehicleData: any): void {
    // Vehicle was created manually with make, model, year
    // Emit to parent so it can be added to the vehicle list
    this.saveNewVehicle.emit({
      success: true,
      vehicleData: vehicleData,
    });
  }

  onManualFieldsVisibilityChange(isVisible: boolean): void {
    // Track when manual vehicle fields are shown/hidden
    this.showManualVehicleFields = isVisible;
  }

  onCancelAddVehicle(): void {
    this.cancelAddVehicle.emit();
  }

  /**
   * Attempts to create and save a new vehicle for the current customer, displays notifications, and emits the result.
   * @example
   * nSaveNewVehicle()
   * void
   * @param {{void}} {{none}} - No parameters; uses component state (this.customerId) and the addVehicleComponent to perform the action.
   * @returns {{void}} Does not return a value; emits a saveNewVehicle event and shows success/error messages via messageService.
   **/
  onSaveNewVehicle(): void {
    if (!this.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID not found. Please ensure customer is created first.',
      });
      return;
    }

    if (!this.addVehicleComponent) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Vehicle form not found. Please try again.',
      });
      return;
    }

    // Create vehicle using the add vehicle component
    this.addVehicleComponent.createVehicleForCustomer(this.customerId).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vehicle added successfully!',
          });

          // Emit the created vehicle data to parent
          this.saveNewVehicle.emit({
            success: true,
            vehicleData: response.data,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Creation Failed',
            detail: response.message || 'Failed to add vehicle. Please try again.',
          });

          this.saveNewVehicle.emit({
            success: false,
            error: response.message || 'Failed to add vehicle',
          });
        }
      },
      error: error => {
        console.error('Error adding vehicle:', error);
        const errorMessage = extractErrorMessage(error, 'Failed to add vehicle. Please try again.');
        this.messageService.add({
          severity: 'error',
          summary: 'Creation Failed',
          detail: errorMessage,
        });

        this.saveNewVehicle.emit({
          success: false,
          error: errorMessage,
        });
      },
    });
  }

  onBackClick(): void {
    this.back.emit();
  }

  onCancelClick(): void {
    this.cancelFlow.emit();
  }
}
